import Section from "@/pages/settings/components/sections/Section";
import { SettingItemLayoutProps } from "@/pages/settings/components/settingsItems/SettingItemLayout";
import AddActionButton from "@/components/ui/add-action-button";
import DraggableReasonListContainer from "@/pages/settings/components/shared/reasons/DraggableReasonListContainer";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import Input from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import { cn } from "@/utils/utils";
import {
  useClosedLostReasons,
  useCreateClosedLostReason,
  useUpdateClosedLostReason,
  useDeleteClosedLostReason,
  useUpdateClosedLostReasonsOrder
} from "@/entities/settings/deal/hooks";
import { ClosedLostReason } from "@/entities/settings/types";
import {
  useAccountSettings,
  useUpdateAccountSettingByKey
} from "@/entities/settings/hooks";
import { resolveBooleanAccountSetting } from "@/entities/settings/accountSettingDefaults";
import { extractKeyValuePairs } from "@/pages/settings/pages/GeneralSettings";

const TRANSLATION_KEY_TABS = "settings.deals.tabs";

export const GeneralTab: React.FC = () => {
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [newReasonValue, setNewReasonValue] = useState("");
  const [localClosedLostReasons, setLocalClosedLostReasons] = useState<
    ClosedLostReason[]
  >([]);

  const accountSettingsQuery = useAccountSettings();
  const accountSettings = accountSettingsQuery.data;
  const updateSettingsByKey = useUpdateAccountSettingByKey();

  const closedLostReasonsQuery = useClosedLostReasons();
  const createMutation = useCreateClosedLostReason();
  const updateMutation = useUpdateClosedLostReason();
  const deleteMutation = useDeleteClosedLostReason();
  const updateOrderMutation = useUpdateClosedLostReasonsOrder();

  const keyValuePairs = accountSettings
    ? extractKeyValuePairs(accountSettings)
    : {};
  const isChecked = resolveBooleanAccountSetting(
    "auto_mark_stuck_deals",
    keyValuePairs?.auto_mark_stuck_deals
  );

  useEffect(() => {
    if (closedLostReasonsQuery.data?.closedLostReasons) {
      setLocalClosedLostReasons(closedLostReasonsQuery.data.closedLostReasons);
    }
  }, [closedLostReasonsQuery.data]);

  const closedLostReasons = localClosedLostReasons;

  const handleAddNewReason = async () => {
    if (newReasonValue.trim()) {
      await createMutation.mutateAsync({
        data: {
          name: newReasonValue.trim()
        }
      });
      await closedLostReasonsQuery.refetch();
      setNewReasonValue("");
    }
  };

  const handleEdit = async (id: string, newTitle: string) => {
    const previousReasons = [...localClosedLostReasons];
    setLocalClosedLostReasons((prev) =>
      prev.map((reason) =>
        reason.id.toString() === id ? { ...reason, name: newTitle } : reason
      )
    );

    try {
      await updateMutation.mutateAsync({
        pathParams: {
          closedLostReasonId: id
        },
        data: {
          name: newTitle
        }
      });
    } catch {
      setLocalClosedLostReasons(previousReasons);
    }
  };

  const handleDelete = async (id: string) => {
    const previousReasons = [...localClosedLostReasons];
    setLocalClosedLostReasons((prev) =>
      prev.filter((reason) => reason.id.toString() !== id)
    );

    try {
      await deleteMutation.mutateAsync({
        pathParams: {
          closedLostReasonId: id
        }
      });
    } catch (error) {
      setLocalClosedLostReasons(previousReasons);
      throw error;
    }
  };

  const handleItemsChange = async (updatedItems: ClosedLostReason[]) => {
    const previousItems = [...localClosedLostReasons];
    setLocalClosedLostReasons(updatedItems);

    try {
      const orderedIds = updatedItems.map((item) => item.id);
      await updateOrderMutation.mutateAsync({
        data: orderedIds
      });
    } catch {
      setLocalClosedLostReasons(previousItems);
    }
  };

  const handleCheckedChange = (checked: boolean) => {
    updateSettingsByKey.mutate({
      pathParams: { settingKey: "auto_mark_stuck_deals" },
      data: {
        value: checked
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddNewReason();
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setNewReasonValue("");
  };

  const handleSave = () => {
    if (newReasonValue.trim()) {
      handleAddNewReason();
    }
    setIsEditMode(false);
    setNewReasonValue("");
  };

  return (
    <>
      <Section
        title={t(`${TRANSLATION_KEY_TABS}.generalTab.title`)}
        subTitle={t(`${TRANSLATION_KEY_TABS}.generalTab.subTitle`)}
        action={
          !isEditMode ? (
            <AddActionButton
              label={t(`${TRANSLATION_KEY_TABS}.generalTab.add`)}
              onClick={() => setIsEditMode(true)}
            />
          ) : null
        }
        subTitleBreakAfterSentence
      >
        {isEditMode ? (
          <div>
            <div className="rounded-2xl border border-border-border1 bg-surface-widget-card overflow-hidden">
              <div
                className={cn(
                  "w-full flex flex-col items-start justify-center",
                  "max-w-[800px] pl-1 pr-2 py-4 gap-4",
                  "bg-surface-widget-card",
                  closedLostReasons.length > 0 &&
                    "border-b border-border-border1"
                )}
              >
                <div className="flex flex-row w-full justify-between items-center">
                  <div className="flex flex-row items-center w-full gap-0">
                    <div className="w-4 h-4 flex-shrink-0"></div>
                    <div className="flex flex-col w-full justify-start gap-1">
                      <div className="flex w-full items-stretch gap-6">
                        <Input
                          placeholder={
                            t(
                              `${TRANSLATION_KEY_TABS}.generalTab.addReasonPlaceholder`
                            ) || "Add lost reason"
                          }
                          value={newReasonValue}
                          onChange={(e) => setNewReasonValue(e.target.value)}
                          onKeyDown={handleKeyDown}
                          autoFocus
                          containerClassName="flex-1"
                          className="h-8"
                          disabled={createMutation.isPending}
                        />
                        <div className="flex gap-2">
                          <Button
                            variant="secondary"
                            onClick={handleCancel}
                            disabled={createMutation.isPending}
                          >
                            {t("cancel")}
                          </Button>
                          <Button
                            onClick={handleSave}
                            disabled={createMutation.isPending}
                          >
                            {createMutation.isPending ? "Saving..." : t("save")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {closedLostReasons.map((reason, index) => (
                <div
                  key={reason.id}
                  className={cn(
                    "w-full flex flex-col items-start justify-center",
                    "max-w-[800px] pl-1 pr-2 py-4 gap-4",
                    "bg-surface-widget-card",
                    index !== closedLostReasons.length - 1 &&
                      "border-b border-border-border1"
                  )}
                >
                  <div className="flex flex-row w-full justify-between items-center">
                    <div className="flex flex-row items-center w-full gap-0">
                      <div className="w-4 h-4 flex-shrink-0"></div>
                      <div className="flex flex-col w-full justify-start gap-1">
                        <Text variant="body">{reason.name}</Text>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <DraggableReasonListContainer
            items={closedLostReasons}
            onItemsChange={handleItemsChange}
            onEdit={handleEdit}
            onDelete={handleDelete}
            droppableId="deals-closed-lost-reasons"
          />
        )}
      </Section>
      <Section
        title={t(`${TRANSLATION_KEY_TABS}.generalTab.stuckDealsTitle`)}
        items={
          [
            {
              title: t(
                `${TRANSLATION_KEY_TABS}.generalTab.stuckDealsItemTitle`
              ),
              subTitle: t(
                `${TRANSLATION_KEY_TABS}.generalTab.stuckDealsItemSubTitle`
              ),
              actionType: "switch",
              actionProps: {
                checked: isChecked,
                onCheckedChange: handleCheckedChange
              }
            }
          ] as SettingItemLayoutProps[]
        }
      />
    </>
  );
};
